import React, { useMemo, useState } from 'react';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import CalendarIconDate from '@components/Dates/CalendarIconDate';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwIcon from '@components/Gui/NwIcon';

import ModelsTableColumnEventDate from './ModelsTableColumnEventDate';
import ModelsTableColumnNonBookingEvent from './ModelsTableColumnNonBookingEvent';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DatesContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
`;

const VerticalDateButton = styled.div`
    cursor: ${(props) => (props.inEvent ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.inEvent ? '.5' : '1')};
    margin: 0 0.5rem 0.5rem 0;
    position: relative;
`;
const EllipsisContainer = styled.div`
    position: absolute;
    top: 0;
    right: -1rem;

    svg {
        font-size: 1.5rem;
        --fa-primary-color: white;
        --fa-secondary-color: ${(props) => props.theme.DarkerBorderColor};
        --fa-primary-opacity: 1;
        --fa-secondary-opacity: 1;
        cursor: pointer;
    }
`;
const CheckContainer = styled.div`
    position: absolute;
    top: 0;
    right: -1rem;

    svg {
        font-size: 1.5rem;
        --fa-primary-color: white;
        --fa-secondary-color: ${(props) => props.theme.DarkerBorderColor};
        --fa-primary-opacity: 1;
        --fa-secondary-opacity: 1;
        cursor: pointer;
    }
`;

const ModelsTableColumnEvent = ({ addModelsAs, availability, availabilityInfo, model }) => {
    const { context } = useContactsSelector();
    const { currentEvent, scope } = context;

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;

    const maxDatesToShow = isMobile ? 3 : 6;

    const [expandDates, setExpandDates] = useState(false);

    const datesToAdd = currentEvent.EventDates.filter((ed) =>
        ed.EventDatesModels.every((edm) => Number(edm.ModelID) !== Number(model.ID))
    ).map((ed) => ed.ID);

    return scope === 'event' ? (
        // column with dates for adding models to a booking event
        <DatesContainer>
            {currentEvent && currentEvent.EventDates && currentEvent.EventDates.length === 1 ? (
                <ModelsTableColumnEventDate
                    eventDate={currentEvent.EventDates[0]}
                    model={model}
                    availability={availability}
                    availabilityInfo={availabilityInfo}
                    addModelsAs={addModelsAs}
                    currentEvent={currentEvent}
                />
            ) : (expandDates || currentEvent.EventDates.length < maxDatesToShow) && availability ? (
                <>
                    {currentEvent.EventDates.map((ed, i) => (
                        <ModelsTableColumnEventDate
                            key={ed.ID}
                            eventDate={ed}
                            model={model}
                            availability={{ ...availability.eventDates[i] }}
                            availabilityInfo={availabilityInfo}
                            addModelsAs={addModelsAs}
                            currentEvent={currentEvent}
                        />
                    ))}
                    {datesToAdd.length > 0 && (
                        <>
                            <NwDividerVertical style={{ height: '4em', paddingRight: '0.5em', top: '0.5em' }} />
                            <ModelsTableColumnEventDate
                                model={model}
                                addModelsAs={addModelsAs}
                                availabilityInfo={availabilityInfo}
                                currentEvent={currentEvent}
                            />
                        </>
                    )}
                </>
            ) : (
                <VerticalDateButton available={true} inEvent={false} onClick={() => setExpandDates(true)}>
                    <CalendarIconDate date={currentEvent.EventDates[0].FromDate} stacked />
                    {availability.inEvent === 2 ? (
                        <CheckContainer>
                            <NwIcon primary icon={byPrefixAndName.fass['circle-check']} />
                        </CheckContainer>
                    ) : (
                        availability.inEvent === 1 && (
                            <EllipsisContainer>
                                <NwIcon primary icon={byPrefixAndName.fass['circle-ellipsis']} />
                            </EllipsisContainer>
                        )
                    )}
                </VerticalDateButton>
            )}
        </DatesContainer>
    ) : (
        // column with buttons to add models to a non-booking event
        <DatesContainer>
            <ModelsTableColumnNonBookingEvent
                model={model}
                availability={availability}
                currentEvent={currentEvent}
                availabilityInfo={availabilityInfo}
            />
        </DatesContainer>
    );
};

export default ModelsTableColumnEvent;
